import { Box, Button, Link, Text } from '@factorialco/deprecated-design-system'
import { CandidateSourceTagFragment } from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import Selector from 'modules/core/components/Selector'
import { useForm } from 'modules/core/hooks/useForm'
import type { FormErrors } from 'modules/core/hooks/useForm'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useUpdateApplication } from 'modules/ats/hooks/applications/useUpdateApplication'
import { useCandidateSources } from 'modules/ats/hooks/candidateSources/useCandidateSources'
import { useAtsContext } from 'modules/ats/lib/atsContext'
import { CandidateSources, getSourceLabel } from 'modules/ats/lib/utils/candidateSources'

interface EditSourceFormValues {
  sourceId: string
}
interface Props {
  application: CandidateSourceTagFragment
  onClose: () => void
}

export const EditSourceModal = ({ onClose, application }: Props) => {
  const { loading: loadingCandidateSources, candidateSources } = useCandidateSources()
  const { loading, updateApplication } = useUpdateApplication()
  const { navigation } = useNavigation()
  const { canManageAtsSettings } = useAtsContext()

  const footer = canManageAtsSettings ? (
    <Box gap="s8">
      <Text.Normal>
        {i18n.t('ats.candidates.application_view.edit_source.search_message')}
      </Text.Normal>
      <Link.Href href={navigation.settings.ats.fullPath()}>
        <Text.Normal color="grey1000">
          {i18n.t('ats.candidates.application_view.edit_source.create_new_source_link')}
        </Text.Normal>
      </Link.Href>
    </Box>
  ) : null

  const form = useForm<EditSourceFormValues>({
    defaultValues: {
      sourceId: application.applicationSource?.id.toString() || '',
    },
    validate: (data) => {
      const errors: FormErrors<EditSourceFormValues> = {}

      if (!data.sourceId) {
        errors.sourceId = i18n.t('common.empty')
      }

      return errors
    },
    onSubmit: async (data, { setErrors }) => {
      const result = await updateApplication({
        id: application.id,
        sourceId: String(data.sourceId),
      })

      const handled = handleResult(result, (errors) => {
        if (errors) {
          setErrors(errors)
        }
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
        onClose()
      }

      return handled
    },
  })

  const candidateSourcesToShow = candidateSources.filter(
    (source) => source.name !== CandidateSources.Imported
  )

  return (
    <Modal
      title={i18n.t('ats.candidates.application_view.edit_source.modal_title')}
      description={i18n.t('ats.candidates.application_view.edit_source.modal_description')}
      illustration="document-edit"
      onClose={onClose}
      onSubmit={() => form.handleSubmit()}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={loading || loadingCandidateSources} submit>
            {i18n.t('ats.candidates.application_view.edit_source.save_button')}
          </Button>
        </>
      }
    >
      <Box gap="s24">
        <Selector.Popover
          label={i18n.t('ats.candidates.application_view.edit_source.souce_input_label')}
          searchPlaceholder={i18n.t('common.search')}
          placeholder={form.values.sourceId}
          blankLabel={i18n.t('common.empty')}
          value={[form.values.sourceId]}
          onChange={(value) => form.setFieldValue('sourceId', value)}
          mandatory
          error={form.errors.sourceId}
          isDisabled={loadingCandidateSources}
          footer={footer}
        >
          {candidateSourcesToShow.map((option) => (
            <Selector.Option
              key={option.id}
              value={option.id}
              label={getSourceLabel(option.name)}
              searchValue={option.name}
            >
              {getSourceLabel(option.name)}
            </Selector.Option>
          ))}
        </Selector.Popover>
      </Box>
    </Modal>
  )
}
